import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ReplayScoreBoard from '../LiveMatch/replay/ReplayScoreBoard.tsx';
import type { SetResult } from '../LiveMatch/liveMatchTypes.ts';
import type { QuickMatchState, TeamSide } from './quickMatchTypes.ts';
import type { QuickMatchAction } from './quickMatchReducer.ts';
import QuickMatchSetup from './QuickMatchSetup.tsx';

interface QuickMatchBoardProps {
  state: QuickMatchState;
  dispatch: React.Dispatch<QuickMatchAction>;
  onClose: () => void;
}

interface SetupParams {
  homeTeamName: string;
  awayTeamName: string;
  servingTeam: TeamSide;
  pointsToWin: number;
}

function toSetResults(completedSets: QuickMatchState['completedSets']): SetResult[] {
  return completedSets.map((s) => ({
    setNumber: s.setNumber,
    homePoints: s.homePoints,
    awayPoints: s.awayPoints,
    durationMs: 0,
    homeLineup: [],
    awayLineup: [],
  }));
}

export default function QuickMatchBoard({ state, dispatch, onClose }: QuickMatchBoardProps) {
  const [showNewSetSetup, setShowNewSetSetup] = useState(false);

  const setWinner =
    state.phase === 'SET_OVER'
      ? state.homePoints > state.awayPoints
        ? state.homeTeamName
        : state.awayTeamName
      : null;

  const handleNewSet = (params: SetupParams) => {
    dispatch({ type: 'START_NEW_SET', pointsToWin: params.pointsToWin });
    setShowNewSetSetup(false);
  };

  const leftSide: TeamSide = state.swapped ? 'AWAY' : 'HOME';
  const rightSide: TeamSide = state.swapped ? 'HOME' : 'AWAY';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#0f0f1a' }}>
      {/* Tabellone — occupa tutto lo spazio disponibile */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <ReplayScoreBoard
          homeTeamName={state.homeTeamName}
          awayTeamName={state.awayTeamName}
          homePoints={state.homePoints}
          awayPoints={state.awayPoints}
          setsWon={state.setsWon}
          completedSets={toSetResults(state.completedSets)}
          currentSet={state.currentSet}
          servingTeam={state.servingTeam}
          swapped={state.swapped}
        />
      </Box>

      {/* Bottoni +1 compatti */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          px: { xs: 2, sm: 3 },
          pb: 1,
          height: { xs: 80, sm: 96 },
        }}
      >
        <Button
          variant="contained"
          fullWidth
          onClick={() => dispatch({ type: 'ADD_POINT', side: leftSide })}
          disabled={state.phase === 'SET_OVER'}
          sx={{
            fontSize: { xs: '1.4rem', sm: '1.7rem' },
            fontWeight: 900,
            bgcolor: '#4fc3f7',
            color: '#0f0f1a',
            '&:hover': { bgcolor: '#81d4fa' },
            '&:disabled': { bgcolor: 'rgba(79,195,247,0.3)', color: 'rgba(0,0,0,0.3)' },
            borderRadius: 3,
          }}
        >
          +1
        </Button>

        <Button
          variant="contained"
          fullWidth
          onClick={() => dispatch({ type: 'ADD_POINT', side: rightSide })}
          disabled={state.phase === 'SET_OVER'}
          sx={{
            fontSize: { xs: '1.4rem', sm: '1.7rem' },
            fontWeight: 900,
            bgcolor: '#ef9a9a',
            color: '#0f0f1a',
            '&:hover': { bgcolor: '#ffcdd2' },
            '&:disabled': { bgcolor: 'rgba(239,154,154,0.3)', color: 'rgba(0,0,0,0.3)' },
            borderRadius: 3,
          }}
        >
          +1
        </Button>
      </Box>

      {/* Azioni secondarie */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 2,
          pb: 3,
          pt: 1,
          px: 3,
        }}
      >
        <Button
          variant="outlined"
          startIcon={<UndoIcon />}
          onClick={() => dispatch({ type: 'UNDO' })}
          disabled={state.history.length === 0}
          sx={{ color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.3)' }}
        >
          Annulla
        </Button>
        <Button
          variant="outlined"
          startIcon={<SwapHorizIcon />}
          onClick={() => dispatch({ type: 'SWAP_SIDES' })}
          sx={{ color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.3)' }}
        >
          Inverti lati
        </Button>
      </Box>

      {/* Dialog fine set */}
      <Dialog open={state.phase === 'SET_OVER' && !showNewSetSetup} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ textAlign: 'center', pt: 3 }}>
          <Typography variant="h5" fontWeight={700}>
            🏆 Set vinto!
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pb: 1 }}>
          <Typography variant="h6" color="primary" fontWeight={600}>
            {setWinner}
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            {state.homePoints} – {state.awayPoints}
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Set vinti: {state.homeTeamName} {state.setsWon.home} – {state.setsWon.away} {state.awayTeamName}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', gap: 1, px: 3, pb: 3 }}>
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={() => setShowNewSetSetup(true)}
          >
            Nuovo set
          </Button>
          <Button variant="outlined" fullWidth onClick={onClose}>
            Esci dalla partita
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog setup nuovo set */}
      <Dialog open={showNewSetSetup} maxWidth="xs" fullWidth>
        <DialogTitle>
          Configura il prossimo set
          <IconButton
            onClick={() => setShowNewSetSetup(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <SwapHorizIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <QuickMatchSetup
            homeTeamName={state.homeTeamName}
            awayTeamName={state.awayTeamName}
            isNewSet
            onStart={handleNewSet}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}
